import { Intent } from "@blueprintjs/core";
import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { HexTooltip } from "../../../hex-components";
import { ORG_ID } from "../../../orgs.js";
import { Routes } from "../../../route/routes.js";
import { ClientCounterEvent } from "../../../util/clientCounterEvent";
import { trackCounterEvent } from "../../../util/trackEvent";
import { StarIconCheckbox } from "../../common/StarIconCheckbox.js";
import { useToaster } from "../../common/Toasts";
import { useStarCollection } from "../../home/collections-tab/hooks/useStarCollection.js";
import { MenuItemTextWithShowOnHover } from "../../home/sidebar/MenuItemTextWithShowOnHover.js";
import { CollectionIcon } from "../../icons/CustomIcons.js";
import { StarredCollectionForSidebarFragment } from "../FavoriteCollections.generated.js";

import { FixedHeightMenuItem } from "./styled.js";
import { isModifiedEvent } from "./utils.js";

type FavoriteCollectionRowProps = Pick<
  StarredCollectionForSidebarFragment,
  "emoji" | "name" | "id"
> & {
  counterEventName: ClientCounterEvent;
};

export const FavoriteCollectionRow: React.FunctionComponent<
  FavoriteCollectionRowProps
> = ({ counterEventName, emoji, id, name }) => {
  const navigate = useNavigate();
  const toaster = useToaster();

  const handleClick = useCallback(
    (evt: React.MouseEvent) => {
      const withModifier = isModifiedEvent(evt);
      trackCounterEvent(counterEventName);
      if (withModifier) {
        const url = Routes.COLLECTION.href(ORG_ID, true, {
          collectionId: id,
          collectionName: name,
        });
        window.open(url, "_blank");
        return;
      }

      Routes.COLLECTION.push(navigate, {
        collectionId: id,
        collectionName: name,
      });
    },
    [id, name, navigate, counterEventName],
  );

  const handleStarCollectionError = useCallback(
    (isStarred: boolean) => {
      toaster.show({
        message: isStarred
          ? "There was an error removing this collection from your favorites."
          : "There was an error adding this collection to your favorites.",
        intent: Intent.DANGER,
      });
    },
    [toaster],
  );
  const handleStarIconCheckboxChange = useStarCollection(
    id,
    handleStarCollectionError,
    undefined,
  );

  const icon = emoji != null ? <span>{emoji}</span> : <CollectionIcon />;

  const hoverTargetId = useMemo(() => `${id}-sidebar-hover-container`, [id]);
  const starIconCheckboxId = useMemo(
    () => `${id}-sidebar-add-to-favorites`,
    [id],
  );

  return (
    <HexTooltip content={name} targetTagName="div">
      <FixedHeightMenuItem
        icon={icon}
        text={
          <MenuItemTextWithShowOnHover
            hoverContent={
              <StarIconCheckbox
                id={starIconCheckboxId}
                starred={true}
                onChange={handleStarIconCheckboxChange}
              />
            }
            id={hoverTargetId}
            text={name}
          />
        }
        onClick={handleClick}
      />
    </HexTooltip>
  );
};
